import { Link } from "react-router-dom";
import { useUiLang } from "../../../stores/uiLang";

function CoppaPageEn() {
  return (
    <div className="bg-gray-50 min-h-screen pt-20">
      <section className="bg-gradient-to-r from-indigo-700 via-purple-700 to-indigo-800 py-20 px-4 text-white text-center">
        <h1 className="text-4xl font-bold mb-2">COPPA Compliance Statement</h1>
        <p className="text-lg max-w-2xl mx-auto">
          How Project Explore protects the privacy of children under 13 under
          the Children's Online Privacy Protection Act.
        </p>
      </section>

      <section className="py-16 px-6 max-w-4xl mx-auto space-y-10 text-gray-700 text-sm">
        <p className="text-xs text-gray-500">
          <strong>Statute:</strong> 15 U.S.C. §§ 6501–6506; 16 CFR Part 312 &nbsp;|&nbsp;
          <strong>Operator:</strong> Startup Smartup, 2380 California Street #402, San Francisco, CA 94115 &nbsp;|&nbsp;
          <strong>Effective Date:</strong> May 27, 2026
        </p>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">1. Overview</h2>
          <p className="mb-3">
            The Children's Online Privacy Protection Act ("COPPA") and its
            implementing regulations (16 CFR Part 312) impose requirements on
            operators of websites and online services directed to children under
            13 years of age, or that have actual knowledge they are collecting
            personal information from children under 13.
          </p>
          <p>
            Project Explore is an educational platform used in K-12 schools.
            Some students using the platform are under 13. Startup Smartup
            treats this as actual knowledge that it collects personal
            information from children under 13 and complies with COPPA
            accordingly.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            2. School as Consent Agent
          </h2>
          <p className="mb-3">
            Under the FTC's COPPA rule (16 CFR § 312.5(b)(1)), a school may
            consent to the collection of personal information from students on
            behalf of parents where:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2 mb-3">
            <li>The operator's collection is limited to the educational context</li>
            <li>The information is not used to build profiles or for commercial advertising purposes</li>
            <li>The school has reviewed and accepted the operator's privacy practices on behalf of families</li>
          </ul>
          <p className="mb-3">
            When a school or district contracts with Startup Smartup and
            provisions student accounts on Project Explore, the contracting
            school acts as the consent agent for COPPA purposes. Startup
            Smartup does not obtain separate verifiable parental consent
            directly from parents for students enrolled through a contracting
            school or district.
          </p>
          <p>
            Schools and districts using Project Explore are responsible for
            providing appropriate notice to parents in their own annual FERPA
            notification or technology use disclosure, and for ensuring that
            their use of Project Explore is authorized under applicable school
            board policy. Startup Smartup provides language schools may use in
            their COPPA and FERPA disclosures upon request.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            3. Information Collected from Children Under 13
          </h2>
          <p className="mb-3">
            Through the school-consent channel, Project Explore may collect
            the following information from students under 13:
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border border-gray-200 px-3 py-2 text-left font-medium">Data Element</th>
                  <th className="border border-gray-200 px-3 py-2 text-left font-medium">How Collected</th>
                  <th className="border border-gray-200 px-3 py-2 text-left font-medium">Purpose</th>
                </tr>
              </thead>
              <tbody>
                <tr className="even:bg-gray-50">
                  <td className="border border-gray-200 px-3 py-2">First name and last name</td>
                  <td className="border border-gray-200 px-3 py-2">District provisioning or roster import</td>
                  <td className="border border-gray-200 px-3 py-2">Account identification; teacher gradebook</td>
                </tr>
                <tr className="even:bg-gray-50">
                  <td className="border border-gray-200 px-3 py-2">Student email address</td>
                  <td className="border border-gray-200 px-3 py-2">District provisioning (grade 3+ only)</td>
                  <td className="border border-gray-200 px-3 py-2">Login credential; assignment notifications</td>
                </tr>
                <tr className="even:bg-gray-50">
                  <td className="border border-gray-200 px-3 py-2">Grade level</td>
                  <td className="border border-gray-200 px-3 py-2">District provisioning or roster import</td>
                  <td className="border border-gray-200 px-3 py-2">Curriculum tier assignment</td>
                </tr>
                <tr className="even:bg-gray-50">
                  <td className="border border-gray-200 px-3 py-2">Assignment responses and submissions</td>
                  <td className="border border-gray-200 px-3 py-2">Student interaction with platform</td>
                  <td className="border border-gray-200 px-3 py-2">Grading; teacher feedback; progress tracking</td>
                </tr>
                <tr className="even:bg-gray-50">
                  <td className="border border-gray-200 px-3 py-2">Assessment scores</td>
                  <td className="border border-gray-200 px-3 py-2">Platform grading</td>
                  <td className="border border-gray-200 px-3 py-2">Academic progress reporting to teacher and district</td>
                </tr>
                <tr className="even:bg-gray-50">
                  <td className="border border-gray-200 px-3 py-2">Video viewing progress</td>
                  <td className="border border-gray-200 px-3 py-2">Platform analytics</td>
                  <td className="border border-gray-200 px-3 py-2">Completion tracking; instructional support</td>
                </tr>
                <tr className="even:bg-gray-50">
                  <td className="border border-gray-200 px-3 py-2">IEP/504 accommodation flags</td>
                  <td className="border border-gray-200 px-3 py-2">District or teacher entry</td>
                  <td className="border border-gray-200 px-3 py-2">Appropriate instructional support</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-3">
            Startup Smartup does not collect geolocation data, biometric data,
            social security numbers, financial information, or health
            information (other than IEP/504 flags provided by the district)
            from any student.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            4. How Information Is Used
          </h2>
          <p className="mb-3">
            Information collected from students under 13 through Project
            Explore is used exclusively for K-12 educational purposes:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2 mb-3">
            <li>Delivering and personalizing curriculum content</li>
            <li>Grading assignments and generating AI-assisted teacher feedback</li>
            <li>Tracking student progress and reporting to teachers and administrators</li>
            <li>Supporting IEP/504 accommodations</li>
            <li>Maintaining account security and platform integrity</li>
          </ul>
          <p className="mb-3 font-medium">
            Startup Smartup does NOT:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Use student information for behavioral advertising or targeted marketing</li>
            <li>Build commercial profiles from student data</li>
            <li>Sell, rent, trade, or share student data for commercial purposes</li>
            <li>Disclose student data to advertisers</li>
            <li>Use student data to market other products or services to students or parents</li>
          </ul>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            5. Parental Rights Under COPPA
          </h2>
          <p className="mb-3">
            Parents and legal guardians of students under 13 have the
            following rights under COPPA with respect to personal information
            collected through Project Explore:
          </p>
          <p className="mb-2">
            <strong>(a) Right to review.</strong> Parents may review the
            personal information collected from their child. To exercise this
            right, contact your child's school administrator, who can export
            your child's records from the platform. Alternatively, contact
            Startup Smartup directly at{" "}
            <a href="mailto:privacy@startupsmartup.com" className="text-indigo-600 underline">
              privacy@startupsmartup.com
            </a>{" "}
            and we will coordinate with the contracting school to fulfill the
            request within 45 days.
          </p>
          <p className="mb-2">
            <strong>(b) Right to request deletion.</strong> Parents may
            request deletion of their child's personal information from the
            platform. Requests should be directed to the school administrator
            first. Upon a request from the contracting school or district,
            Startup Smartup will delete the student's data within 30 days,
            unless retention is required by applicable law.
          </p>
          <p className="mb-2">
            <strong>(c) Right to refuse further collection.</strong> Parents
            may direct the school to discontinue the student's use of Project
            Explore. A parent who directs the school to remove a student from
            the platform should contact the school administrator; the
            administrator can deactivate the student account.
          </p>
          <p>
            <strong>(d) Right to correction.</strong> Parents may request
            correction of inaccurate information in their child's record by
            contacting the school administrator or Startup Smartup support.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            6. Data Retention
          </h2>
          <p>
            Active student data is retained for the enrollment period plus 90
            days. Following account deactivation, student data is deleted
            within 90 days unless the district requests earlier deletion or
            retention is required by applicable law (e.g., litigation holds).
            Aggregated, de-identified analytics data may be retained
            indefinitely; no individual student can be identified from that
            data.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            7. Security of Children's Data
          </h2>
          <p className="mb-3">
            Startup Smartup implements reasonable security procedures
            appropriate to the nature and sensitivity of student data,
            including:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>AES-256 encryption of data at rest</li>
            <li>TLS 1.2+ encryption of data in transit</li>
            <li>Row Level Security policies that restrict data access to users with a legitimate educational interest</li>
            <li>Role-based access controls (students cannot access other students' records)</li>
          </ul>
          <p className="mt-3">
            See the{" "}
            <Link to="/legal/incident-response" className="text-indigo-600 underline">
              Incident Response Policy
            </Link>{" "}
            for details on how Startup Smartup detects and responds to
            security incidents involving student data.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">8. Contact</h2>
          <p className="mb-2">
            For COPPA-related inquiries, including parental rights requests:
          </p>
          <p>
            <strong>Justin Ghiglia, Privacy Contact</strong>
            <br />
            Startup Smartup
            <br />
            2380 California Street #402, San Francisco, CA 94115
            <br />
            <a href="mailto:privacy@startupsmartup.com" className="text-indigo-600 underline">
              privacy@startupsmartup.com
            </a>
          </p>
        </div>

        <div className="text-xs text-gray-500">
          Effective Date: May 27, 2026
          <br />
          Cross-reference:{" "}
          <Link to="/privacy" className="text-indigo-600 underline">Privacy Policy</Link>{" | "}
          <Link to="/legal/ferpa" className="text-indigo-600 underline">FERPA Statement</Link>{" | "}
          <Link to="/legal/sopipa" className="text-indigo-600 underline">SOPIPA Acknowledgment</Link>{" | "}
          <Link to="/legal/dpa" className="text-indigo-600 underline">Data Processing Agreement</Link>
        </div>
      </section>
    </div>
  );
}

const TH = "border border-gray-200 px-3 py-2 text-left font-medium";
const TD = "border border-gray-200 px-3 py-2";

function CoppaPageEs() {
  return (
    <div className="bg-gray-50 min-h-screen pt-20">
      <section className="bg-gradient-to-r from-indigo-700 via-purple-700 to-indigo-800 py-20 px-4 text-white text-center">
        <h1 className="text-4xl font-bold mb-2">Declaración de cumplimiento de COPPA</h1>
        <p className="text-lg max-w-2xl mx-auto">
          Cómo Project Explore protege la privacidad de los niños menores de 13
          años bajo la Ley de Protección de la Privacidad de los Niños en
          Internet.
        </p>
      </section>

      <section className="py-16 px-6 max-w-4xl mx-auto space-y-10 text-gray-700 text-sm">
        <p className="p-3 bg-indigo-50 border-l-4 border-indigo-500 rounded text-indigo-800">
          Esta página es una traducción al español. Para ver el texto original en inglés, elige EN arriba.
        </p>
        <p className="text-xs text-gray-500">
          <strong>Ley:</strong> 15 U.S.C. §§ 6501 a 6506; 16 CFR Parte 312 &nbsp;|&nbsp;
          <strong>Operador:</strong> Startup Smartup, 2380 California Street #402, San Francisco, CA 94115 &nbsp;|&nbsp;
          <strong>Fecha de entrada en vigor:</strong> 27 de mayo de 2026
        </p>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">1. Resumen</h2>
          <p className="mb-3">
            La Ley de Protección de la Privacidad de los Niños en Internet
            ("COPPA") y su reglamento (16 CFR Parte 312) imponen requisitos a
            los operadores de sitios web y servicios en línea dirigidos a niños
            menores de 13 años, o que saben con certeza que recopilan
            información personal de niños menores de 13 años.
          </p>
          <p>
            Project Explore es una plataforma educativa que se usa en escuelas
            de K-12. Algunos de los estudiantes que la usan tienen menos de 13
            años. Startup Smartup considera esto como conocimiento cierto de
            que recopila información personal de niños menores de 13 años y
            cumple con COPPA en consecuencia.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            2. La escuela como agente de consentimiento
          </h2>
          <p className="mb-3">
            Bajo la regla de COPPA de la FTC (16 CFR § 312.5(b)(1)), una
            escuela puede dar su consentimiento para la recopilación de
            información personal de los estudiantes en nombre de los padres
            cuando:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2 mb-3">
            <li>La recopilación del operador se limita al contexto educativo</li>
            <li>La información no se usa para crear perfiles ni con fines de publicidad comercial</li>
            <li>La escuela revisó y aceptó las prácticas de privacidad del operador en nombre de las familias</li>
          </ul>
          <p className="mb-3">
            Cuando una escuela o un distrito contrata a Startup Smartup y crea
            cuentas de estudiantes en Project Explore, la escuela contratante
            actúa como agente de consentimiento para los fines de COPPA.
            Startup Smartup no obtiene el consentimiento parental verificable
            por separado y directamente de los padres para los estudiantes
            inscritos por medio de una escuela o un distrito contratante.
          </p>
          <p>
            Las escuelas y los distritos que usan Project Explore son
            responsables de dar a los padres un aviso adecuado en su propia
            notificación anual de FERPA o en su divulgación sobre el uso de
            tecnología, y de asegurar que su uso de Project Explore esté
            autorizado bajo la política aplicable de la junta escolar. Si lo
            piden, Startup Smartup les da un texto que las escuelas pueden usar
            en sus divulgaciones de COPPA y FERPA.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            3. Información que se recopila de niños menores de 13 años
          </h2>
          <p className="mb-3">
            Por medio del canal de consentimiento de la escuela, Project
            Explore puede recopilar la siguiente información de estudiantes
            menores de 13 años:
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead className="bg-gray-100">
                <tr>
                  <th className={TH}>Dato</th>
                  <th className={TH}>Cómo se recopila</th>
                  <th className={TH}>Finalidad</th>
                </tr>
              </thead>
              <tbody>
                <tr className="even:bg-gray-50">
                  <td className={TD}>Nombre y apellido</td>
                  <td className={TD}>Alta por parte del distrito o importación de la lista de clase</td>
                  <td className={TD}>Identificación de la cuenta; libro de calificaciones del maestro</td>
                </tr>
                <tr className="even:bg-gray-50">
                  <td className={TD}>Correo electrónico del estudiante</td>
                  <td className={TD}>Alta por parte del distrito (solo de 3.er grado en adelante)</td>
                  <td className={TD}>Credencial de inicio de sesión; avisos de tareas</td>
                </tr>
                <tr className="even:bg-gray-50">
                  <td className={TD}>Grado escolar</td>
                  <td className={TD}>Alta por parte del distrito o importación de la lista de clase</td>
                  <td className={TD}>Asignación del nivel del currículo</td>
                </tr>
                <tr className="even:bg-gray-50">
                  <td className={TD}>Respuestas y trabajos entregados</td>
                  <td className={TD}>Interacción del estudiante con la plataforma</td>
                  <td className={TD}>Calificación; comentarios del maestro; seguimiento del progreso</td>
                </tr>
                <tr className="even:bg-gray-50">
                  <td className={TD}>Calificaciones de evaluaciones</td>
                  <td className={TD}>Calificación de la plataforma</td>
                  <td className={TD}>Informes de progreso académico para el maestro y el distrito</td>
                </tr>
                <tr className="even:bg-gray-50">
                  <td className={TD}>Avance en los videos</td>
                  <td className={TD}>Estadísticas de la plataforma</td>
                  <td className={TD}>Seguimiento de lo completado; apoyo a la enseñanza</td>
                </tr>
                <tr className="even:bg-gray-50">
                  <td className={TD}>Indicadores de adaptaciones IEP/504</td>
                  <td className={TD}>Captura por parte del distrito o del maestro</td>
                  <td className={TD}>Apoyo adecuado para la enseñanza</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-3">
            Startup Smartup no recopila de ningún estudiante datos de
            geolocalización, datos biométricos, números de Seguro Social,
            información financiera ni información de salud (aparte de los
            indicadores de IEP/504 que proporciona el distrito).
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            4. Cómo se usa la información
          </h2>
          <p className="mb-3">
            La información que Project Explore recopila de estudiantes menores
            de 13 años se usa exclusivamente con fines educativos de K-12:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2 mb-3">
            <li>Ofrecer y personalizar el contenido del currículo</li>
            <li>Calificar tareas y generar comentarios para el maestro con ayuda de IA</li>
            <li>Dar seguimiento al progreso de los estudiantes e informar a maestros y administradores</li>
            <li>Apoyar las adaptaciones IEP/504</li>
            <li>Mantener la seguridad de las cuentas y la integridad de la plataforma</li>
          </ul>
          <p className="mb-3 font-medium">
            Startup Smartup NO:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Usa la información de los estudiantes para publicidad conductual ni mercadotecnia dirigida</li>
            <li>Crea perfiles comerciales con los datos de los estudiantes</li>
            <li>Vende, alquila, intercambia ni comparte datos de estudiantes con fines comerciales</li>
            <li>Divulga datos de estudiantes a anunciantes</li>
            <li>Usa datos de estudiantes para ofrecer otros productos o servicios a los estudiantes o a los padres</li>
          </ul>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            5. Derechos de los padres bajo COPPA
          </h2>
          <p className="mb-3">
            Los padres y tutores legales de estudiantes menores de 13 años
            tienen los siguientes derechos bajo COPPA respecto de la
            información personal recopilada por medio de Project Explore:
          </p>
          <p className="mb-2">
            <strong>(a) Derecho a revisar.</strong> Los padres pueden revisar
            la información personal que se recopiló de su hijo. Para ejercer
            este derecho, comunícate con el administrador de la escuela de tu
            hijo, quien puede exportar los registros de tu hijo desde la
            plataforma. También puedes comunicarte directamente con Startup
            Smartup en{" "}
            <a href="mailto:privacy@startupsmartup.com" className="text-indigo-600 underline">
              privacy@startupsmartup.com
            </a>{" "}
            y coordinaremos con la escuela contratante para cumplir la
            solicitud en un plazo de 45 días.
          </p>
          <p className="mb-2">
            <strong>(b) Derecho a pedir la eliminación.</strong> Los padres
            pueden pedir que se elimine de la plataforma la información
            personal de su hijo. Las solicitudes deben dirigirse primero al
            administrador de la escuela. Cuando la escuela o el distrito
            contratante lo pida, Startup Smartup eliminará los datos del
            estudiante en un plazo de 30 días, salvo que la ley aplicable exija
            conservarlos.
          </p>
          <p className="mb-2">
            <strong>(c) Derecho a negarse a que se siga recopilando.</strong>{" "}
            Los padres pueden indicar a la escuela que el estudiante deje de
            usar Project Explore. El padre que indique a la escuela que retire
            al estudiante de la plataforma debe comunicarse con el
            administrador escolar; el administrador puede desactivar la cuenta
            del estudiante.
          </p>
          <p>
            <strong>(d) Derecho a la corrección.</strong> Los padres pueden
            pedir que se corrija la información inexacta del registro de su
            hijo comunicándose con el administrador de la escuela o con el
            soporte de Startup Smartup.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            6. Conservación de datos
          </h2>
          <p>
            Los datos de los estudiantes activos se conservan durante el
            período de inscripción más 90 días. Después de desactivar la cuenta,
            los datos del estudiante se eliminan en un plazo de 90 días, salvo
            que el distrito pida una eliminación más temprana o que la ley
            aplicable exija conservarlos (por ejemplo, por órdenes de
            conservación en un litigio). Los datos de análisis agregados y sin
            identificar pueden conservarse de forma indefinida; con esos datos
            no se puede identificar a ningún estudiante.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            7. Seguridad de los datos de los niños
          </h2>
          <p className="mb-3">
            Startup Smartup aplica procedimientos de seguridad razonables,
            adecuados a la naturaleza y lo delicado de los datos de los
            estudiantes, entre ellos:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Cifrado AES-256 de los datos en reposo</li>
            <li>Cifrado TLS 1.2+ de los datos en tránsito</li>
            <li>Políticas de seguridad a nivel de fila que limitan el acceso a los datos a los usuarios con un interés educativo legítimo</li>
            <li>Controles de acceso por función (los estudiantes no pueden entrar a los registros de otros estudiantes)</li>
          </ul>
          <p className="mt-3">
            Consulta la{" "}
            <Link to="/legal/incident-response" className="text-indigo-600 underline">
              Política de respuesta a incidentes
            </Link>{" "}
            para ver cómo Startup Smartup detecta y responde a los incidentes
            de seguridad que involucran datos de estudiantes.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">8. Contacto</h2>
          <p className="mb-2">
            Para consultas relacionadas con COPPA, incluidas las solicitudes de
            derechos de los padres:
          </p>
          <p>
            <strong>Justin Ghiglia, contacto de privacidad</strong>
            <br />
            Startup Smartup
            <br />
            2380 California Street #402, San Francisco, CA 94115
            <br />
            <a href="mailto:privacy@startupsmartup.com" className="text-indigo-600 underline">
              privacy@startupsmartup.com
            </a>
          </p>
        </div>

        <div className="text-xs text-gray-500">
          Fecha de entrada en vigor: 27 de mayo de 2026
          <br />
          Referencias cruzadas:{" "}
          <Link to="/privacy" className="text-indigo-600 underline">Política de privacidad</Link>{" | "}
          <Link to="/legal/ferpa" className="text-indigo-600 underline">Declaración de FERPA</Link>{" | "}
          <Link to="/legal/sopipa" className="text-indigo-600 underline">Reconocimiento de SOPIPA</Link>{" | "}
          <Link to="/legal/dpa" className="text-indigo-600 underline">Acuerdo de procesamiento de datos</Link>
        </div>
      </section>
    </div>
  );
}

export default function CoppaPage() {
  return useUiLang() === "es" ? <CoppaPageEs /> : <CoppaPageEn />;
}
